import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@tellescope/ui";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  CLASSIFICATION_OPTIONS,
  getClassificationTranslations,
  COMPARISON_TYPE_OPTIONS,
  getComparisonTypeTranslations,
  TREND_NONE_VALUE,
  getTrendOptions,
} from "./vitalConfigurationConstants";
import type { RangeRow, VitalComparisonType } from "./useVitalConfigurationForm";

export interface SortableRangeRowProps {
  row: RangeRow;
  onRemove: (id: string) => void;
  onChange: (id: string, updates: Partial<RangeRow>) => void;
}

export const SortableRangeRow = ({ row, onRemove, onChange }: SortableRangeRowProps) => {
  const { t } = useTranslation("vitals");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 rounded-md border bg-background px-2 py-2">
      <Button
        variant="ghost"
        size="icon"
        className="cursor-grab touch-none text-muted-foreground h-6 w-6 shrink-0"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </Button>

      <Select value={row.classification} onValueChange={(v) => onChange(row.id, { classification: v })}>
        <SelectTrigger className="w-[160px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CLASSIFICATION_OPTIONS.map((c) => (
            <SelectItem key={c} value={c}>
              {getClassificationTranslations()[c]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={row.comparisonType}
        onValueChange={(v) => onChange(row.id, { comparisonType: v as VitalComparisonType })}
      >
        <SelectTrigger className="w-[150px]">
          <SelectValue placeholder={t("Operator")} />
        </SelectTrigger>
        <SelectContent>
          {COMPARISON_TYPE_OPTIONS.map((c) => (
            <SelectItem key={c} value={c}>
              {getComparisonTypeTranslations()[c]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {row.comparisonType === "Between" ? (
        <div className="flex items-center gap-1 flex-1">
          <Input
            className="flex-1"
            type="number"
            placeholder={t("Lower")}
            value={row.valueLower}
            onChange={(e) => onChange(row.id, { valueLower: e.target.value })}
          />
          <span className="text-muted-foreground text-sm">—</span>
          <Input
            className="flex-1"
            type="number"
            placeholder={t("Upper")}
            value={row.valueUpper}
            onChange={(e) => onChange(row.id, { valueUpper: e.target.value })}
          />
        </div>
      ) : (
        <Input
          className="flex-1"
          type="number"
          placeholder={t("Value")}
          value={row.value}
          onChange={(e) => onChange(row.id, { value: e.target.value })}
        />
      )}

      <Select
        value={row.trendIntervalInMS || TREND_NONE_VALUE}
        onValueChange={(v) => onChange(row.id, { trendIntervalInMS: v === TREND_NONE_VALUE ? "" : v })}
      >
        <SelectTrigger className="w-[160px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent position="item-aligned" className="max-h-60">
          {getTrendOptions().map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        variant="ghost"
        size="icon"
        className="text-muted-foreground h-7 w-7 shrink-0"
        onClick={() => onRemove(row.id)}
        aria-label={t("Remove range")}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
